import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../shared/utils";
import { QuestionProgress } from "../../onboarding/components/QuestionProgress";
import { QuestionRenderer } from "../../onboarding/components/QuestionRenderer";
import { AssessmentNavigation } from "../../onboarding/components/AssessmentNavigation";
import { useAssessmentFlow } from "../../onboarding/hooks/useAssessmentFlow";
import { transformAnswersToResponses } from "../../onboarding/hooks";
import { usePreRegistrationQuestions } from "../queries";
import { useSubmitPreRegistrationResponses } from "../mutations";

type Step = "email" | "questions" | "success";

export const RegisterInterest: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("email");

  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const [clientId, setClientId] = useState<string | null>(null);
  const { data, isLoading, error } = usePreRegistrationQuestions(clientId);
  const submitMutation = useSubmitPreRegistrationResponses();

  const questions = useMemo(() => data?.questions ?? [], [data]);

  const {
    answers,
    currentAnswer,
    currentIndex,
    currentQuestion,
    goToNext,
    goToPrevious,
    isCurrentAnswerValid,
    progress,
    setAnswer,
  } = useAssessmentFlow({ questions, initialAnswers: {} });

  const qrCodeQuestionId = useMemo(() => {
    const qrQuestion = questions.find((question) => question.type === "qrcode");
    return qrQuestion?.id ?? null;
  }, [questions]);

  const qrCodeValue =
    qrCodeQuestionId && typeof answers[qrCodeQuestionId] === "string"
      ? String(answers[qrCodeQuestionId]).trim()
      : "";
  const isUuid = useCallback(
    (value: string) =>
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
        value
      ),
    []
  );

  const handleQrCodeChange = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      setClientId(trimmed && isUuid(trimmed) ? trimmed : null);
    },
    [isUuid]
  );

  useEffect(() => {
    if (!qrCodeValue) return;
    if (!isUuid(qrCodeValue)) return;
    setClientId(qrCodeValue);
  }, [isUuid, qrCodeValue]);

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);
  const isEmailValid = emailRegex.test(email);
  const showEmailError = emailTouched && !isEmailValid;

  const handleEmailContinue = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailTouched(true);
    setSubmissionError(null);

    if (!isEmailValid) return;
    setStep("questions");
  };

  const handleBack = useCallback(() => {
    setSubmissionError(null);
    if (step === "questions" && currentIndex === 0) {
      setStep("email");
      return;
    }
    if (step === "email") {
      navigate(ROUTES.LOGIN);
      return;
    }
    goToPrevious();
  }, [currentIndex, goToPrevious, navigate, step]);

  const handleNext = useCallback(async () => {
    if (!currentQuestion) return;
    setSubmissionError(null);

    const isLastQuestion = currentIndex === questions.length - 1;

    if (!isLastQuestion) {
      goToNext();
      return;
    }

    try {
      await submitMutation.mutateAsync({
        email: email.trim(),
        answers: transformAnswersToResponses(answers, questions),
      });
      setStep("success");
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to save your responses. Please try again.";
      setSubmissionError(message);
    }
  }, [
    answers,
    currentIndex,
    currentQuestion,
    email,
    goToNext,
    questions,
    submitMutation,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          width: "100%",
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading questions...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          p: 4,
          width: "100%",
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load questions. Please try again.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Error: {error instanceof Error ? error.message : "Unknown error"}
        </Typography>
      </Box>
    );
  }

  if (step === "success") {
    return (
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          borderColor: "divider",
          minHeight: 0,
          overflow: "hidden",
          maxHeight: "100vh",
        }}
      >
        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <CheckCircleOutlineRoundedIcon color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h5" component="h1" fontWeight={600}>
              Thanks — you’re registered
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We’ve saved your responses. We’ll be in touch soon.
            </Typography>
            <Button
              size="large"
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => navigate(ROUTES.LOGIN)}
              sx={{ mt: 2 }}
            >
              Back to login
            </Button>
          </Stack>
        </Box>
      </Card>
    );
  }

  if (step === "email") {
    return (
      <Card
        component="form"
        onSubmit={handleEmailContinue}
        variant="outlined"
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          borderColor: "divider",
          minHeight: 0,
          overflow: "hidden",
          maxHeight: "100vh",
        }}
      >
        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={3.5}>
            <Stack spacing={1.5}>
              <Typography variant="h5" component="h2" fontWeight={600}>
                Register your interest
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your email and answer a few quick questions.
              </Typography>
            </Stack>

            {submissionError && (
              <Alert severity="error" onClose={() => setSubmissionError(null)}>
                {submissionError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email address"
              value={email}
              type="email"
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setEmailTouched(true)}
              error={showEmailError}
              helperText={showEmailError ? "Please enter a valid email address." : ""}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              disabled={!email.length || submitMutation.isPending}
              sx={{ mt: 2 }}
            >
              Continue
            </Button>

            <Link
              component={RouterLink}
              to={ROUTES.LOGIN}
              variant="body2"
              underline="hover"
              sx={{ alignSelf: "center", fontWeight: 500 }}
            >
              Back to sign in
            </Link>
          </Stack>
        </Box>
      </Card>
    );
  }

  if (!currentQuestion) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: { md: "md", xs: "100%" },
        height: "100%",
        mx: "auto",
      }}
    >
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          borderColor: "divider",
          minHeight: 0,
          overflow: "hidden",
          maxHeight: "100vh",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: { xs: 3, md: 4 },
            pb: 0,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <QuestionProgress
            currentStep={currentIndex + 1}
            totalSteps={questions.length}
            progress={progress}
          />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack spacing={3.5} sx={{ flexGrow: 1, minHeight: "fit-content" }}>
            {submissionError && (
              <Alert severity="error" onClose={() => setSubmissionError(null)}>
                {submissionError}
              </Alert>
            )}

            <Stack spacing={1.5}>
              <Typography variant="h5" component="h2" fontWeight={600}>
                {currentQuestion.title}
              </Typography>
              {currentQuestion.subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {currentQuestion.subtitle}
                </Typography>
              )}
              {currentQuestion.helperText && (
                <Typography variant="body2" color="text.secondary">
                  {currentQuestion.helperText}
                </Typography>
              )}
            </Stack>

            <Box sx={{ flexGrow: 1, minHeight: "fit-content" }}>
              <QuestionRenderer
                question={currentQuestion}
                value={currentAnswer}
                onChange={(updated) => {
                  setAnswer(currentQuestion.id, updated);
                  if (currentQuestion.type === "qrcode") {
                    handleQrCodeChange(String(updated || ""));
                  }
                }}
              />
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 3, md: 4 },
            pt: 2,
            pb: { xs: 2.5, md: 3 },
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            position: "sticky",
            bottom: 0,
            zIndex: 1,
          }}
        >
          <AssessmentNavigation
            disableNext={!isCurrentAnswerValid || submitMutation.isPending}
            isFinalStep={currentIndex === questions.length - 1}
            finalLabel="Submit"
            onBack={handleBack}
            onNext={handleNext}
          />
        </Box>
      </Card>
    </Box>
  );
};
